import { useMemo } from "react";
import { Accordion } from "../../../accordion";
import { Paper } from "../../../paper";
import { Skeleton } from "../../../skeleton";
import { Text } from "../../../text";
import { kebabCase } from "lodash";
import { useInteractionModalContext } from "../hooks";

/**
 * Renders the right-side details panel with collapsible accordion sections for interaction metadata.
 * @returns {JSX.Element} The DetailsSidebar component.
 */
export const DetailsSidebar = () => {
    const context = useInteractionModalContext();
    return context.sidebar.sections?.map((section) => {
        if (section.isLoading) {
            return (
                <Paper key={section.key} p="lg">
                    <Skeleton height={20} width="100%" />
                </Paper>
            );
        }

        const panelContent = typeof section.content === "function" ? section.content(context) : section.content;
        const headerElement =
            typeof section.header === "function" ? section.header(context) : <>{section.header ?? null}</>;

        if (panelContent === null || typeof panelContent === "undefined") {
            return null;
        }

        return (
            <Paper
                key={section.key}
                p="xs"
                bd="1px solid var(--border-default-color)"
                rootStackGap={typeof section.header !== "undefined" ? "sm" : undefined}
                className="details-sidebar__paper-container"
                id={kebabCase(`interaction-modal-section-${section.key}`)}
            >
                {headerElement}
                <Accordion variant="single" defaultValue={section.isOpenByDefault ? section.key : undefined}>
                    <Accordion.Item key={section.key} value={section.key}>
                        <Accordion.Control
                            data-testid={kebabCase(`details-sidebar-${section.title}-accordion-trigger`)}
                            id={kebabCase(`details-sidebar-${section.title}-accordion-trigger`)}
                            onClick={() => {
                                section?.onToggleAccordion?.();
                            }}
                        >
                            <Text
                                variant="semibold"
                                size="md"
                                data-testid={kebabCase(`details-sidebar-${section.title}-accordion-title`)}
                                id={kebabCase(`details-sidebar-${section.title}-accordion-title`)}
                            >
                                {section.title}
                            </Text>
                        </Accordion.Control>
                        <Accordion.Panel
                            pt="xs"
                            data-testid={kebabCase(`details-sidebar-${section.title}-accordion-content`)}
                            className={kebabCase(`details-sidebar-${section.title}-accordion-content`)}
                        >
                            {panelContent}
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </Paper>
        );
    });
};
